import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!user) {
      setWishlist({ items: [] });
      return;
    }
    
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setWishlist(data);
    } catch (e) {
      console.error('Error fetching wishlist:', e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      setWishlist({ items: [] });
    }
  }, [user, fetchWishlist]);

  const addToWishlist = async (productId) => {
    if (!user) {
      return { success: false, error: 'Please login to add items to wishlist' };
    }

    try {
      await axios.post(`${API}/wishlist/add`, { product_id: productId }, { withCredentials: true });
      await fetchWishlist();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to add to wishlist' };
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
      await fetchWishlist();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to remove from wishlist' };
    }
  };

  const isInWishlist = (productId) => {
    return wishlist.items.some(item => item.product_id === productId);
  };

  return (
    <WishlistContext.Provider value={{ 
      wishlist, 
      loading, 
      addToWishlist, 
      removeFromWishlist, 
      isInWishlist,
      fetchWishlist 
    }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
}
